import type { ApiErrorBody } from "@hostdock/types";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly requestId: string | null,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }

  get isUnauthenticated() {
    return this.status === 401;
  }
  get isForbidden() {
    return this.status === 403 && this.code === "FORBIDDEN";
  }
  /** Field-level validation messages keyed by dotted path. */
  get fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {};
    if (this.code === "VALIDATION_ERROR" && Array.isArray(this.details)) {
      for (const d of this.details as { path?: string; message?: string }[]) {
        if (d.path && d.message && !out[d.path]) out[d.path] = d.message;
      }
    }
    return out;
  }
}

export type Query = Record<string, string | number | boolean | null | undefined>;

export interface RequestOptions {
  body?: unknown;
  /** Raw bytes sent as application/octet-stream (File Manager upload chunks). */
  raw?: Blob | ArrayBuffer;
  query?: Query;
  idempotencyKey?: string;
  signal?: AbortSignal;
}

export interface ApiClientOptions {
  baseUrl?: string;
  /** Called on any 401 so the app can drop cached session state. */
  onUnauthenticated?: (error: ApiError) => void;
}

export function buildQuery(query?: Query): string {
  if (!query) return "";
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined && v !== null && v !== "") params.set(k, String(v));
  }
  const s = params.toString();
  return s ? `?${s}` : "";
}

export class ApiClient {
  private csrfToken = "";
  constructor(private readonly options: ApiClientOptions = {}) {}

  setCsrfToken(token: string | null | undefined) {
    this.csrfToken = token ?? "";
  }

  url(path: string, query?: Query) {
    return `${this.options.baseUrl ?? ""}${path}${buildQuery(query)}`;
  }

  async request<T>(method: string, path: string, opts: RequestOptions = {}): Promise<T> {
    const headers: Record<string, string> = { accept: "application/json" };
    if (opts.raw !== undefined) headers["content-type"] = "application/octet-stream";
    else if (opts.body !== undefined) headers["content-type"] = "application/json";
    if (method !== "GET" && this.csrfToken) headers["x-csrf-token"] = this.csrfToken;
    if (opts.idempotencyKey) headers["idempotency-key"] = opts.idempotencyKey;

    let res: Response;
    try {
      res = await fetch(this.url(path, opts.query), {
        method,
        headers,
        credentials: "same-origin",
        body: opts.raw !== undefined ? opts.raw : opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
        signal: opts.signal,
      });
    } catch (err) {
      if ((err as Error).name === "AbortError") throw err;
      throw new ApiError(0, "NETWORK_ERROR", "Cannot reach HostDock. Check your connection and try again.", null);
    }

    if (res.status === 204) return undefined as T;
    const text = await res.text();
    let data: unknown = null;
    try {
      data = text ? JSON.parse(text) : null;
    } catch {
      data = null;
    }
    if (!res.ok) {
      const body = data as Partial<ApiErrorBody> | null;
      const error = new ApiError(
        res.status,
        body?.error?.code ?? `HTTP_${res.status}`,
        body?.error?.message ?? `Request failed (${res.status})`,
        body?.error?.requestId ?? res.headers.get("x-request-id"),
        body?.error?.details,
      );
      if (res.status === 401) this.options.onUnauthenticated?.(error);
      throw error;
    }
    return data as T;
  }

  get = <T>(path: string, query?: Query, signal?: AbortSignal) => this.request<T>("GET", path, { query, signal });
  post = <T>(path: string, body: unknown = {}, opts: Omit<RequestOptions, "body"> = {}) => this.request<T>("POST", path, { ...opts, body });
  put = <T>(path: string, body: unknown) => this.request<T>("PUT", path, { body });
  patch = <T>(path: string, body: unknown) => this.request<T>("PATCH", path, { body });
  delete = <T = void>(path: string, body?: unknown) => this.request<T>("DELETE", path, body !== undefined ? { body } : {});
  putRaw = <T>(path: string, raw: Blob | ArrayBuffer, query?: Query, signal?: AbortSignal) => this.request<T>("PUT", path, { raw, query, signal });
}

export const newIdempotencyKey = () => `hd-${crypto.randomUUID()}`;
